import ActionTypes from "../constants/ActionTypes";
import axiosInstance from "../helpers/refreshTokenMiddleware";
import BASE_URL from "../constants/Api";

/* =========================================================
 * 詳細検索リクエスト生成（共通）
 * ========================================================= */
const buildDetailSearchRequestBody = (criteria, deptId, folderId, key = null) => {
    const requestBody = {
        is_delete: 0,
        department_id: deptId,
        folder_id: folderId,
        and_flag: criteria.searchMode === "all" ? 1 : 0,
        key: key ?? null,
    };

    let fields = [];
    switch (deptId) {
        case "D001":
            fields = [
                "auto_number",
                "product_no",
                "destination",
                "customer",
                "assignee",
                "product_model",
                "variety_name",
                "remarks",
                "title",
            ];
            break;
        case "D002":
            fields = [
                "auto_number",
                "product_no",
                "destination",
                "customer",
                "device_name",
                "assembly_type",
                "assignee",
                "remarks",
                "title",
            ];
            break;
        case "D003":
            fields = [
                "auto_number",
                "category_name",
                "product_name",
                "product_model",
                "purpose",
                "customer",
                "assignee",
                "original_doc_number",
                "remarks",
                "title",
            ];
            break;
        default:
            break;
    }

    fields.forEach((field) => {
        requestBody[field] = {
            option: Number(criteria[`${field}_condition`]) || 0,
            value: criteria[field] || "",
            ambiguous: criteria[`${field}_fuzzy`] ? 1 : 0,
        };
    });

    // ===== 日付条件 =====
    const dateCond = criteria.dateCondition;

    const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    if (!dateCond || dateCond.isAll) {
        requestBody.issue_datetime_start = null;
        requestBody.issue_datetime_end = null;
    } else if (dateCond.mode === "date") {
        requestBody.issue_datetime_start = dateCond.dateFrom
            ? formatDate(new Date(dateCond.dateFrom))
            : null;
        requestBody.issue_datetime_end = dateCond.dateTo
            ? formatDate(new Date(dateCond.dateTo))
            : null;
    } else if (dateCond.mode === "past") {
        const today = new Date();
        const past = new Date();
        past.setDate(today.getDate() - Number(dateCond.pastDays || 0));
        requestBody.issue_datetime_start = formatDate(past);
        requestBody.issue_datetime_end = formatDate(today);
    }

    requestBody.document_category = {
        option: 2,
        value: criteria.document_category === "" ? "" : Number(criteria.document_category),
        ambiguous: 0,
    };

    requestBody.document_type = {
        option: 2,
        value: criteria.document_type === "" ? "" : Number(criteria.document_type),
        ambiguous: 0,
    };

    if (criteria.data_limit !== "unlimited") {
        requestBody.data_limit = Number(criteria.data_limit) || 50;
    }

    return requestBody;
};

/* =========================================================
 * 詳細検索
 * ========================================================= */
export const fetchDetailSearchRequest = () => ({
    type: ActionTypes.FETCH_DETAIL_SEARCH_REQUEST,
});

export const fetchDetailSearchSuccess = (data, headers) => ({
    type: ActionTypes.FETCH_DETAIL_SEARCH_SUCCESS,
    payload: { data, headers },
});

export const fetchDetailSearchFailure = (error) => ({
    type: ActionTypes.FETCH_DETAIL_SEARCH_FAILURE,
    payload: error,
});

/**
 * 詳細検索データ取得
 * @param {object} criteria - 全検索条件
 * @param {string} deptId - 部署ID
 * @param {string} folderId - フォルダID
 */
export const fetchDetailSearch = (criteria, deptId, folderId, key = null) => async (dispatch) => {
    dispatch(fetchDetailSearchRequest());

    try {
        const requestBody = buildDetailSearchRequestBody(criteria, deptId, folderId, key);

        const response = await axiosInstance.post(`${BASE_URL}/top/detail-search`, requestBody);

        const { data, headers, last_key } = response.data.respons || {};

        dispatch(fetchDetailSearchSuccess(data || [], headers || [], last_key));
    } catch (error) {
        console.error("fetchDetailSearch error:", error);
        dispatch(fetchDetailSearchFailure(error.message || "詳細検索中にエラーが発生しました。"));
    }
};

export const fetchDetailSearchCountRequest = () => ({
    type: ActionTypes.FETCH_DETAIL_SEARCH_COUNT_REQUEST,
});

export const fetchDetailSearchCountSuccess = (data) => ({
    type: ActionTypes.FETCH_DETAIL_SEARCH_COUNT_SUCCESS,
    payload: data,
});

export const fetchDetailSearchCountFailure = (error) => ({
    type: ActionTypes.FETCH_DETAIL_SEARCH_COUNT_FAILURE,
    payload: error,
});

export const fetchDetailSearchCount = (criteria, deptId, folderId) => async (dispatch) => {
    dispatch(fetchDetailSearchCountRequest());
    try {
        const requestBody = buildDetailSearchRequestBody(criteria, deptId, folderId, null);

        const response = await axiosInstance.post(
                `${BASE_URL}/top/detail-search-count`,
                requestBody
            );

        const { count, keys } = response.data.respons|| {};

        dispatch(fetchDetailSearchCountSuccess({ count, keys }));

    } catch (error) {
        dispatch(fetchDetailSearchCountFailure(error.message || "件数取得中にエラーが発生しました。"));
    }
};

/* =========================================================
 * 図面種類・図面タイプ取得 (/top/data-new)
 * ========================================================= */
export const fetchDataTypeRequest = () => ({
  type: ActionTypes.FETCH_DATA_TYPE_REQUEST,
});

export const fetchDataTypeSuccess = (document_category, document_type) => ({
  type: ActionTypes.FETCH_DATA_TYPE_SUCCESS,
  payload: { document_category, document_type },
});

export const fetchDataTypeFailure = (error) => ({
  type: ActionTypes.FETCH_DATA_TYPE_FAILURE,
  payload: error,
});

export const fetchDataType = (departmentId, folderId = null) => async (dispatch) => {
  dispatch(fetchDataTypeRequest());

  try {
    const response = await axiosInstance.post(`${BASE_URL}/top/data-new`, {
      department_id: departmentId,
      folder_id: folderId ?? null, 
    });

    const { document_category = [], document_type = [] } = response.data.respons || {};
    dispatch(fetchDataTypeSuccess(document_category, document_type));
  } catch (error) {
    console.error("fetchDataType error:", error.response?.status, error.message);
    dispatch(fetchDataTypeFailure(error.message || "データ取得中にエラーが発生しました。"));
  }

};
